// Stroke's tissue state per structure, for a viewport that draws no sim volumes (wgpu has no `volume:` pass yet). ba-app's
// table (pipelines/hemo/tissue_by_structure.py) counts each tissue-fate run's 2 mm voxels per structure of IXI 165's label
// map: the share of each structure's volume in penumbra, core and reperfused, at the run's own frame times where they
// change. A structure is coloured by those shares (the lead's rule, 10-06): the states' colours (design's scale.tissue-fate)
// mixed by their shares, over its own colour as far as its volume is in any of them, in 10 % steps. A structure 20 %
// infarcted is a fifth of the way to the core colour; one 90 % infarcted nearly all of it.
//
// It goes through the viewport's `overlay`, whose opacity is one for all its keys: one overlay per step, at that step's
// opacity. Each structure is on the overlay of its step at each time and NaN (its own colour) on the others. The overlay's
// value places the mix on a colormap through the three colours, so two states mix by their shares.

import { SimFiles, type SimSource } from './files.ts';
import type { Overlay } from './overlay.ts';
import { TISSUE_FATE } from '../../ui/scales.ts';

/** The table under sims/. A new version is a new id: sims/ are immutable once published. */
export const TISSUE_BY_STRUCTURE = 'hemo/ixi165-stroke-tissue-by-structure-v1';

/** The Methods line, the lead's words (10-06): how a structure's colour sums up the run's voxels, and the table it comes from. */
export const TISSUE_TINT_METHODS = `Colour per structure: its own colour, tinted by the tissue states in its volume. The tint mixes the states’ colours by their shares, and its strength is the share in any state, in 10 % steps (${TISSUE_BY_STRUCTURE.slice('hemo/'.length)}).`;

/** The tint's steps: a structure's colour moves a tenth of the way to its states' colour for each tenth of its volume in them. */
export const TINT_STEPS = 10;

/** A structure's shares of its volume (its 2 mm tissue voxels), one per listed time. */
export interface StructureShares {
  voxels: number;
  penumbra: number[];
  core: number[];
  reperfused: number[];
}

/** One run's table: the times its states change (s from occlusion) and its structures' shares there. */
export interface TissueTable {
  run: string;
  times: number[];
  structures: Record<string, StructureShares>;
}

/** One step of the tint, as the viewport takes it. */
export type TissueTint = Pick<Overlay, 'id' | 'keys' | 'times' | 'values' | 'colormap' | 'range'> & { opacity: number };

interface TableHeader {
  files?: { runs?: Record<string, string> };
}

interface TableFile {
  run?: string;
  times_s?: number[];
  structures?: Record<string, StructureShares>;
}

/** A run's table, or null where there's none: no table on this server, or none for this run. Quiet, as a missing optional file is. */
export async function loadTissueTable(src: SimSource, run: string): Promise<TissueTable | null> {
  const dir = new SimFiles(src, TISSUE_BY_STRUCTURE);
  const file = (await dir.maybeJson<TableHeader>('header.json'))?.files?.runs?.[run];
  if (!file) return null;
  const t = await dir.maybeJson<TableFile>(file);
  if (!t || t.run !== run || !Array.isArray(t.times_s) || !t.structures) return null;
  return { run, times: t.times_s, structures: t.structures };
}

/** A structure's step: the share of its volume in any of the three states, to the nearest tenth (0: no tint). */
export const stepOf = (penumbra: number, core: number, reperfused: number): number => Math.min(TINT_STEPS, Math.floor((penumbra + core + reperfused) * TINT_STEPS + 0.5));

/**
 * Where a structure's mix of states sits on the tint's colormap: penumbra at 0, core at 1, reperfused at 2 and penumbra
 * again at 3, two states between theirs by their shares. All three at once (no run has it) mix the larger two. Null for none.
 */
export function hueOf(penumbra: number, core: number, reperfused: number): number | null {
  const share = [penumbra, core, reperfused];
  const on = [0, 1, 2].filter((i) => share[i]! > 0);
  if (on.length === 3) on.splice(share.indexOf(Math.min(...share)), 1);
  const [a, b] = on;
  if (a === undefined) return null;
  if (b === undefined) return a;
  // Reperfused and penumbra meet across the colormap's wrap, from 2 to 3.
  if (a === 0 && b === 2) return 2 + penumbra / (penumbra + reperfused);
  return a + share[b]! / (share[a]! + share[b]!);
}

/** The tint's colormap over 0–3: penumbra, core, reperfused and back to penumbra. */
export const tintColormap = (colours: Record<string, string> = TISSUE_FATE): TissueTint['colormap'] => ({
  stops: [
    [0, colours.penumbra!],
    [1 / 3, colours.core!],
    [2 / 3, colours['reperfused-saved']!],
    [1, colours.penumbra!],
  ],
});

/** How long before each listed time a structure's value changes, s: overlays are linear between their times, and a run's
 *  states hold until the next. */
const HOLD_S = 1e-3;

/** A run's tint: one overlay per step some structure takes, each at that step's opacity. */
export function tissueTints(t: TissueTable, colours: Record<string, string> = TISSUE_FATE): TissueTint[] {
  const n = t.times.length;
  const keys = Object.keys(t.structures).filter((k) => {
    const s = t.structures[k]!;
    return s.penumbra?.length === n && s.core?.length === n && s.reperfused?.length === n;
  });
  if (!n || !keys.length) return [];
  const K = keys.length;
  const steps = new Uint8Array(n * K);
  const hues = new Float32Array(n * K);
  for (let k = 0; k < K; k++) {
    const s = t.structures[keys[k]!]!;
    for (let i = 0; i < n; i++) {
      steps[i * K + k] = stepOf(s.penumbra[i]!, s.core[i]!, s.reperfused[i]!);
      hues[i * K + k] = hueOf(s.penumbra[i]!, s.core[i]!, s.reperfused[i]!) ?? NaN;
    }
  }
  // Each listed time, and just before the next one, so that each value holds until its state changes.
  const times: number[] = [];
  const frames: number[] = [];
  for (let i = 0; i < n; i++) {
    if (i > 0 && t.times[i]! - HOLD_S > t.times[i - 1]!) {
      times.push(t.times[i]! - HOLD_S);
      frames.push(i - 1);
    }
    times.push(t.times[i]!);
    frames.push(i);
  }
  const colormap = tintColormap(colours);
  const used = [...new Set(steps)].filter((x) => x > 0).sort((a, b) => a - b);
  return used.map((step) => {
    const values = new Float32Array(times.length * K).fill(NaN);
    frames.forEach((i, f) => {
      for (let k = 0; k < K; k++) if (steps[i * K + k] === step) values[f * K + k] = hues[i * K + k]!;
    });
    return { id: `tissue:${step}`, keys, times, values, colormap, range: [0, 3] as [number, number], opacity: step / TINT_STEPS };
  });
}
